import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@heroui/react';
import { useShallow } from 'zustand/react/shallow';
import { useDataStore } from '@/stores/dataStore';
import { avatarHue } from '@/lib/avatarHue';
import { isGuildMuted, subscribeMuteSettings } from '@/lib/muteSettings';
import { PlusIcon } from '@/components/icons';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { DownloadAppButton } from '@/components/ui/DownloadAppButton';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { BugReportModal } from '@/components/beta/BugReportModal';
import { BunkerLogo } from '@/components/BunkerLogo';
import { platformOwnsIdentity } from '@/lib/platformIdentity';
import { ServerModal } from '@/components/ServerModal';
import { ServerContextMenu } from '@/heroui/sidebar/ServerContextMenu';
import { InviteToServerModal } from '@/heroui/sidebar/InviteToServerModal';
import { onAppEvent } from '@/lib/appEvents';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { FolderTile } from '@/heroui/FolderTile';
import { useExpandedFolders, toggleFolderExpanded } from '@/lib/guildFolderCollapse';
import { CloseIcon, EditIcon, TrashIcon } from '@/components/icons';
import type { GuildFolder } from '@/types/api';
import { applyRailReorder } from '@/heroui/guildRail/railReorder';
import { GuildAvatarButton } from '@/heroui/guildRail/GuildAvatarButton';
import { ServerOverflowStack } from '@/heroui/guildRail/ServerOverflowStack';
import { planGuildRail, railMaxWidth, folderRailWidth, RESERVED_FIXED_PX, STACK_COMPACT_PX } from '@/heroui/guildRail/railCapacity';
import { FolderShelf } from '@/heroui/guildRail/FolderShelf';
import { railFolderCaption, railFolderLabel } from '@/heroui/guildRail/folderRules';
import { selectUnreadByGuild, selectVoiceByGuild } from '@/heroui/guildRail/railSelectors';
import { FolderSettingsModal } from '@/heroui/guildRail/FolderSettingsModal';
import { TooltipTarget } from '@/components/ui/TooltipTarget';
import { useIsNarrowViewport } from '@/lib/useIsNarrowViewport';

// Adaptive-overflow geometry lives in guildRail/railCapacity.ts (pure + tested);
// the expanded-folder shelf lives in guildRail/FolderShelf.tsx.
/** Sensible first-paint guess for the action cluster, corrected on measure. */
const ACTIONS_FALLBACK_PX = 170;

/** Discord-style active marker for the vertical rail: a tall bar
 *  flush to the rail's left edge — flat left, rounded right — sitting in a
 *  full-width row so it clears the avatar with a gap. */
const RAIL_BAR_CLASS =
  'pointer-events-none absolute -left-1.5 top-1/2 z-20 h-10 w-1 -translate-y-1/2 rounded-r-full bg-accent';

/**
 * HeroUI-native server rail for the HeroApp shell.
 *
 * Adaptive layout: when every server fits the header width they all render
 * inline (static, one-tap switching). When they don't fit we show as many as
 * fit plus a "+N" stack chip that opens a searchable popover with the full
 * list — so a quick switch stays one tap and a big server count never pushes
 * the DM tile / ＋ / theme toggle off-screen. The active server is always kept
 * among the inline ones.
 *
 * Drag-and-drop reorder: each server avatar is draggable via native HTML5 D&D.
 * An optimistic reorder is applied to the store immediately; the API call
 * persists it. The overflow stack also supports drag-out (dragging a server
 * from the "+N" popover moves it to the visible area position).
 */
export function HeroTopBar({ vertical = false, compact = false }: { vertical?: boolean; compact?: boolean } = {}) {
  const { t } = useTranslation();
  // The rail foot shows the light/dark AND the RU/EN toggles side by side,
  // in the top and the left layouts alike.
  // Inside the platform frame the host owns the brand, download and language.
  // Bunker keeps its own feedback and appearance controls in the rail.
  const embedded = platformOwnsIdentity();
  // On a phone theme and language live in Settings → Appearance; the rail
  // keeps its width for servers (fewer of them fold into "+N").
  const narrow = useIsNarrowViewport();
  const [betaOpen, setBetaOpen] = useState(false);
  // The left rail has a small, regular control dock; the horizontal rail keeps
  // its single-row controls. All four actions remain reachable on short views.
  const railToggle = (
    <div className={vertical ? 'grid grid-cols-2 gap-x-0 gap-y-1' : 'flex items-center gap-1'}>
      <Tooltip delay={0}>
        <TooltipTarget>
          <button
            type="button"
            aria-label={t('beta.title')}
            onClick={() => setBetaOpen(true)}
            className="beta-label-trigger grid size-11 place-items-center rounded-lg text-[10px] font-semibold text-ink-dim transition-colors hover:bg-default/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
          >
            <span className="beta-label" aria-hidden="true">
              <span className="beta-label-letter">B</span>
              <span className="beta-label-letter">e</span>
              <span className="beta-label-letter">t</span>
              <span className="beta-label-letter">a</span>
            </span>
          </button>
        </TooltipTarget>
        <Tooltip.Content>{t('beta.tooltip')}</Tooltip.Content>
      </Tooltip>
      {/* Web-only: offer the Windows installer. Self-hides inside the Electron
          shell and the mobile app, so it never nags someone who already has it. */}
      {!embedded && !narrow && <DownloadAppButton />}
      {!narrow && <ThemeToggle className="grid size-11 place-items-center p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" />}
      {!embedded && !narrow && <LanguageToggle className="grid size-11 place-items-center p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" />}
      <BugReportModal open={betaOpen} onClose={() => setBetaOpen(false)} />
    </div>
  );
  const guildOrder = useDataStore((s) => s.guildOrder);
  const guilds = useDataStore((s) => s.guilds);
  const currentGuildId = useDataStore((s) => s.currentGuildId);
  const selectGuild = useDataStore((s) => s.selectGuild);
  const goHome = useDataStore((s) => s.goHome);
  const reorderGuilds = useDataStore((s) => s.reorderGuilds);
  // Per-guild badge numbers (unread, people in voice), not the store-wide maps.
  const unreadByGuild = useDataStore(useShallow(selectUnreadByGuild));
  const voiceByGuild = useDataStore(useShallow(selectVoiceByGuild));
  const guildFolders = useDataStore((s) => s.guildFolders);
  const setGuildFolders = useDataStore((s) => s.setGuildFolders);
  const upsertGuildFolder = useDataStore((s) => s.upsertGuildFolder);
  const removeGuildFolder = useDataStore((s) => s.removeGuildFolder);
  const expandedFolders = useExpandedFolders();
  const [folderMenu, setFolderMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  /** Folder whose name/colour dialog is open. */
  const [editFolderId, setEditFolderId] = useState<string | null>(null);
  const [dropFolderId, setDropFolderId] = useState<string | null>(null);
  const folderOfGuild = useMemo(() => {
    const map = new Map<string, GuildFolder>();
    for (const f of guildFolders) for (const gid of f.guildIds) map.set(gid, f);
    return map;
  }, [guildFolders]);
  // Resolved from the store, not captured on open: dissolving the folder in
  // another tab (or dropping its last server) must close the dialog, not leave
  // it editing something that no longer exists.
  const editFolder = editFolderId
    ? guildFolders.find((f) => f.id === editFolderId) ?? null
    : null;
  const [modalOpen, setModalOpen] = useState(false);
  const [serverTab, setServerTab] = useState<'create' | 'join'>('create');
  const [inviteGuildId, setInviteGuildId] = useState<string | null>(null);

  // Let any UI (e.g. the home screen) open the add-server modal on a tab.
  useEffect(() =>
    onAppEvent('server:open', (d) => {
      setServerTab(d.tab ?? 'create');
      setModalOpen(true);
    }), []);
  // Mute state lives in localStorage (lib/muteSettings); subscribe so the
  // rail re-renders the moment a server is muted/unmuted from the context menu.
  const [, bumpMute] = useReducer((n: number) => n + 1, 0);
  useEffect(() => subscribeMuteSettings(bumpMute), []);
  const [menu, setMenu] = useState<{ guildId: string; x: number; y: number } | null>(
    null,
  );

  // --- native HTML5 drag reorder state ---
  // Ref mirrors the dragging id so dragOver — which fires rapidly — can read
  // the current value without a stale closure.
  const draggingIdRef = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [dragOverPos, setDragOverPos] = useState<'before' | 'after' | 'merge'>('before');

  // Debounced persist — fire the API call 300 ms after the last reorder so we
  // batch multiple rapid drags into a single network round-trip.
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistOrder = useCallback((order: string[]) => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      api.reorderGuilds(order).catch(() => {
        toast.error(t('ui.topbar.reorderFailed'));
      });
    }, 300);
  }, [t]);

  function applyReorder(draggedId: string, targetId: string, pos: 'before' | 'after') {
    const next = applyRailReorder(guildOrder, draggedId, targetId, pos);
    if (next === guildOrder) return;
    reorderGuilds(next);
    persistOrder(next);
  }

  function endDrag() {
    draggingIdRef.current = null;
    setDragOverId(null);
    setDropFolderId(null);
  }

  // ---- server folders ----------------------------------------------------
  const refetchFolders = useCallback(() => {
    api
      .listGuildFolders()
      .then((r) => setGuildFolders(r.folders))
      .catch((err) => toast.error(...errToToast(err, t('ui.topbar.folderLoadFailed'))));
  }, [setGuildFolders, t]);

  /** The merge gesture: drop a server on the centre of another. Creates a new
   *  folder from the two, or adds the dragged server to the target's folder. */
  function mergeOnto(draggedId: string, targetId: string) {
    if (draggedId === targetId) return;
    const targetFolder = folderOfGuild.get(targetId);
    if (targetFolder) {
      addToFolder(targetFolder.id, draggedId);
      return;
    }
    // New folder from [target, dragged]; the API returns it with a real id.
    api
      .createGuildFolder({ guildIds: [targetId, draggedId] })
      .then((folder) => upsertGuildFolder(folder))
      .catch((err) => toast.error(...errToToast(err, t('ui.topbar.folderCreateFailed'))));
  }

  function addToFolder(folderId: string, guildId: string) {
    // Optimistic: append to the target folder, pull out of any other folder.
    setGuildFolders(
      guildFolders.map((f) =>
        f.id === folderId
          ? { ...f, guildIds: f.guildIds.includes(guildId) ? f.guildIds : [...f.guildIds, guildId] }
          : { ...f, guildIds: f.guildIds.filter((g) => g !== guildId) },
      ),
    );
    api.addGuildFolderEntry(folderId, guildId).catch((err) => {
      toast.error(...errToToast(err, t('ui.topbar.folderAddFailed')));
      refetchFolders();
    });
  }

  function removeFromFolder(folderId: string, guildId: string) {
    const folder = guildFolders.find((f) => f.id === folderId);
    if (!folder) return;
    const nextIds = folder.guildIds.filter((g) => g !== guildId);
    // Discord parity: a folder with <2 servers dissolves.
    if (nextIds.length < 2) removeGuildFolder(folderId);
    else upsertGuildFolder({ ...folder, guildIds: nextIds });
    api.removeGuildFolderEntry(folderId, guildId).catch((err) => {
      toast.error(...errToToast(err, t('ui.topbar.folderRemoveFailed')));
      refetchFolders();
    });
  }

  function dissolveFolder(folderId: string) {
    removeGuildFolder(folderId);
    setFolderMenu(null);
    api.deleteGuildFolder(folderId).catch((err) => {
      toast.error(...errToToast(err, t('ui.topbar.folderDissolveFailed')));
      refetchFolders();
    });
  }

  // Drop position from the pointer: horizontal rail splits the avatar into
  // left/centre/right thirds (before/merge/after); the vertical rail
  // splits it top/centre/bottom along the Y axis instead.
  const dropPosFromEvent = (e: React.DragEvent): 'before' | 'after' | 'merge' => {
    const rect = e.currentTarget.getBoundingClientRect();
    const along = vertical ? e.clientY - rect.top : e.clientX - rect.left;
    const size = vertical ? rect.height : rect.width;
    const third = size / 3;
    return along < third ? 'before' : along > third * 2 ? 'after' : 'merge';
  };

  /** Native drag props for a draggable server avatar button. */
  function dragProps(id: string) {
    return {
      draggable: true as const,
      onDragStart: (e: React.DragEvent) => {
        // Set text/plain so the browser doesn't show a forbidden cursor
        // when dragging over non-drop targets outside the rail.
        e.dataTransfer.setData('text/plain', id);
        e.dataTransfer.effectAllowed = 'move';
        draggingIdRef.current = id;
        setDragOverId(null);
      },
      onDragOver: (e: React.DragEvent) => {
        const current = draggingIdRef.current;
        if (!current || current === id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        // Thirds: before / merge (make/join a folder) / after — axis-aware.
        const pos = dropPosFromEvent(e);
        setDragOverId(id);
        setDragOverPos(pos);
      },
      onDragLeave: () => {
        // Only clear if this element was the active over-target.
        setDragOverId((prev) => (prev === id ? null : prev));
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        const current = draggingIdRef.current;
        if (!current || current === id) return;
        const pos = dropPosFromEvent(e);
        if (pos === 'merge') mergeOnto(current, id);
        else applyReorder(current, id, pos);
        endDrag();
      },
      onDragEnd: () => endDrag(),
    };
  }

  // Measure the header so we know how many server avatars fit. Start from the
  // viewport width so the first paint shows all (avoids an overflow flash),
  // then the ResizeObserver corrects it.
  const headerRef = useRef<HTMLElement | null>(null);
  const [headerWidth, setHeaderWidth] = useState(() =>
    typeof window === 'undefined' ? 1280 : window.innerWidth,
  );
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setHeaderWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Width of the right-hand action cluster, measured rather than assumed: it
  // differs per user (moderator-only Beta button) and per platform (no download
  // link in the desktop shell), and guessing it is what let the avatars run
  // underneath it on a narrow screen.
  const actionsRef = useRef<HTMLDivElement | null>(null);
  const [actionsWidth, setActionsWidth] = useState(ACTIONS_FALLBACK_PX);
  useEffect(() => {
    const el = actionsRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setActionsWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Folders render first (always visible); ungrouped servers fill the rest and
  // overflow into the "+N" stack. Position-anchored folder interleave is a
  // later refinement — folders-first keeps the existing overflow logic intact.
  const ungroupedOrder = useMemo(
    () => guildOrder.filter((id) => !folderOfGuild.has(id)),
    [guildOrder, folderOfGuild],
  );

  // Space the folders really take. An expanded folder is its glyph plus every
  // member tile, so budgeting one slot per folder (what both callers did) made
  // the rail clip the servers the folder had just revealed.
  const foldersPx = useMemo(
    () =>
      folderRailWidth(
        guildFolders.map((f) => ({
          expanded: expandedFolders.has(f.id),
          // Only members that resolve to a real guild get a tile — renderFolder
          // filters the same way, so the budget matches what is drawn.
          memberCount: f.guildIds.filter((id) => !!guilds[id]).length,
        })),
      ),
    [guildFolders, expandedFolders, guilds],
  );

  const { visibleIds, overflowIds, stackPreviews, compactStack } = useMemo(() => {
    // Vertical rail scrolls — every server renders, no "+N" overflow.
    if (vertical) {
      return {
        visibleIds: ungroupedOrder,
        overflowIds: [] as string[],
        stackPreviews: 3,
        compactStack: false,
      };
    }
    return planGuildRail({
      ungroupedOrder,
      headerWidth,
      actionsWidth,
      foldersPx,
      currentGuildId,
    });
  }, [ungroupedOrder, headerWidth, actionsWidth, currentGuildId, foldersPx, vertical]);

  // On a narrow screen with enlarged text, keep a complete server picker
  // instead of clipping the always-visible folders or the overflow counter.
  const tightRail = !vertical && headerWidth - RESERVED_FIXED_PX - actionsWidth < foldersPx + STACK_COMPACT_PX;

  function renderGuild(id: string, inFolder = false) {
    const g = guilds[id];
    if (!g) return null;
    const hue = avatarHue(id);
    const unread = unreadByGuild[id] ?? 0;
    const active = id === currentGuildId;
    const muted = isGuildMuted(id);
    const isOver = dragOverId === id;
    const isDragging = draggingIdRef.current === id;
    const btn = (
      <GuildAvatarButton
        key={id}
        guild={g}
        hue={hue}
        unread={unread}
        voiceCount={voiceByGuild[id] ?? 0}
        active={active}
        muted={muted}
        isOver={isOver}
        dragOverPos={dragOverPos}
        isDragging={isDragging}
        dragHandlers={dragProps(id)}
        onSelect={() => selectGuild(id)}
        onOpenMenu={(x, y) => setMenu({ guildId: id, x, y })}
        vertical={vertical}
        // Top-level vertical servers show the left pill (below), so suppress the
        // pulsing halo there — the pulse is reserved for active servers INSIDE a
        // folder, which have no room for the edge pill.
        hideActiveIndicator={vertical && !inFolder}
      />
    );
    // Horizontal rail, or a server nested inside a folder: render the avatar
    // as-is. Top-level vertical rail: wrap in a full-width row so the active
    // bar can pin to the rail's left edge with a gap to the avatar.
    if (!vertical || inFolder) return btn;
    return (
      <div key={id} className={'group relative flex min-h-14 w-[72px] items-center justify-center rounded-[6px] transition-colors ' + (active ? 'bg-accent/10' : 'hover:bg-default/40')}>
        {/* Morphing left pill (Discord-style): a tall bar when active, a small
            dot for unread, nothing otherwise — and it grows to a medium notch on
            hover. Height animates so switching servers feels alive. */}
        <span
          aria-hidden
          className={
            'pointer-events-none absolute -left-1.5 top-1/2 z-20 w-1 -translate-y-1/2 rounded-r-full bg-accent transition-[height] duration-200 ease-out ' +
            (active
              ? 'h-10'
              : unread > 0 && !muted
                ? 'h-2 group-hover:h-5'
                : 'h-0 group-hover:h-5')
          }
        />
        {btn}
      </div>
    );
  }

  function renderFolder(folder: GuildFolder) {
    const members = folder.guildIds.map((id) => guilds[id]).filter((g): g is NonNullable<typeof g> => !!g);
    if (members.length === 0) return null;
    const expanded = expandedFolders.has(folder.id);
    const unread = members.reduce((sum, g) => sum + (unreadByGuild[g.id] ?? 0), 0);
    const active = !!currentGuildId && members.some((g) => g.id === currentGuildId);
    const tint = folder.color ?? 'rgb(var(--bk-accent))';
    const memberNames = members.map((g) => g.name);
    const label = railFolderLabel(folder, memberNames);
    const openMenuAt = (x: number, y: number) => setFolderMenu({ id: folder.id, x, y });
    const dropProps = {
      onDragOver: (e: React.DragEvent) => {
        const current = draggingIdRef.current;
        if (!current) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropFolderId(folder.id);
      },
      onDragLeave: () => setDropFolderId((prev) => (prev === folder.id ? null : prev)),
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        const current = draggingIdRef.current;
        if (current) addToFolder(folder.id, current);
        endDrag();
      },
    };
    /** The folder's servers, each with its "remove from folder" ✕. */
    const memberTiles = members.map((g) => (
      // `flex` (not the default block): the GuildAvatarButton is wrapped in
      // a Tooltip.Trigger that renders display:inline-block, whose baseline
      // descender varies with content (text fallback vs image-only) — in a
      // block wrapper that made image-icon servers 6px taller and pushed
      // them 4px higher than letter ones inside this items-center row.
      <div key={g.id} className="group/fm relative flex">
        {renderGuild(g.id, true)}
        <button
          type="button"
          aria-label={t('ui.topbar.removeFromFolder', { name: g.name })}
          onClick={() => removeFromFolder(folder.id, g.id)}
          className="absolute -right-1 -top-1 z-20 grid size-4 place-items-center rounded-lg bg-danger text-white opacity-0 transition-opacity group-hover/fm:opacity-100 group-focus-within/fm:opacity-100 [@media(pointer:coarse)]:opacity-100"
        >
          <CloseIcon className="size-2.5" />
        </button>
      </div>
    ));

    // Vertical rail, expanded: the shelf (caption + colour spine + hairline)
    // instead of a tinted blob around the group. The head tile is gone here —
    // the servers are already on screen, so a tile that only means "this folder
    // is open" was one avatar-sized slot spent saying nothing.
    if (vertical && expanded) {
      return (
        <FolderShelf
          key={folder.id}
          caption={railFolderCaption(folder)}
          tint={tint}
          label={label}
          isDropTarget={dropFolderId === folder.id}
          onToggle={() => toggleFolderExpanded(folder.id)}
          onContextMenu={(e) => {
            e.preventDefault();
            openMenuAt(e.clientX, e.clientY);
          }}
          onContextMenuAt={openMenuAt}
          dropProps={dropProps}
        >
          {memberTiles}
        </FolderShelf>
      );
    }

    return (
      // Unified container (Discord-style подложка): when expanded, the folder
      // head + its servers share ONE tinted rounded backdrop. Collapsed, the
      // container is invisible and the head tile carries its own tint.
      <div
        key={folder.id}
        className={[
          // rounded-[6px] is applied in BOTH states on purpose: the tinted
          // backdrop fades via the background-color transition, but border-radius
          // is not transitionable here, so toggling the class mid-fade flashed
          // square corners (~0.5s). Keeping the radius constant (invisible while
          // the bg is transparent) removes that flash.
          'flex shrink-0 items-center rounded-[6px] transition-[gap,padding,background-color]',
          vertical ? 'flex-col' : '',
          expanded ? 'gap-2 px-1.5 py-1.5' : 'gap-2.5',
        ].join(' ')}
        style={
          expanded
            ? { backgroundColor: `color-mix(in srgb, ${tint} 14%, transparent)` }
            : undefined
        }
      >
        <FolderTile
          folder={folder}
          vertical={vertical}
          members={members}
          expanded={expanded}
          unread={expanded ? 0 : unread}
          voiceCount={expanded ? 0 : members.reduce((sum, g) => sum + (voiceByGuild[g.id] ?? 0), 0)}
          active={active}
          isDropTarget={dropFolderId === folder.id}
          onToggle={() => toggleFolderExpanded(folder.id)}
          onContextMenu={(e) => {
            e.preventDefault();
            setFolderMenu({ id: folder.id, x: e.clientX, y: e.clientY });
          }}
          onContextMenuAt={openMenuAt}
          dropProps={dropProps}
        />
        {expanded && memberTiles}
      </div>
    );
  }

  // DM / home tile — the bunker vault-door mark doubles as the home button.
  const dmTile = (
    <Tooltip delay={0}>
      <TooltipTarget>
        <button
          aria-label={t('ui.topbar.directMessages')}
          type="button"
          onClick={() => goHome()}
          // Home is marked by a still accent tone (the old WebGL halo span a
          // three.js loop for as long as DMs were open).
          className={'studio-home-control relative flex shrink-0 rounded-[6px] transition-colors ' + (currentGuildId === null ? 'bg-accent/10' : 'hover:bg-default/40')}
        >
          <span className="relative z-10 grid size-12 place-items-center text-foreground">
            <BunkerLogo className="h-auto w-8 drop-shadow-[0_1px_4px_rgba(0,0,0,0.55)]" />
          </span>
        </button>
      </TooltipTarget>
      <Tooltip.Content showArrow placement={vertical ? 'right' : 'bottom'}>
        <Tooltip.Arrow />
        {t('ui.topbar.directMessages')}
      </Tooltip.Content>
    </Tooltip>
  );

  const addButton = (
    <Tooltip delay={0}>
      <TooltipTarget>
        {/* An empty tile, not a circle. Every server in this rail is a 48px
            squircle; a perfect circle was the one different shape in the
            column and read as a control borrowed from elsewhere. The dashed
            edge says "a server could go here" — same size and radius as the
            thing it would become.
            A plain button, like its neighbours GuildAvatarButton and
            FolderTile: HeroUI's Button hard-codes its own radius and size in
            `.button--icon-only` / `.button--lg`, which beat the utilities, so
            it can't take the rail's shape. */}
        <button
          aria-label={t('ui.topbar.addServer')}
          type="button"
          data-tour="add-server"
          onClick={() => { setServerTab('create'); setModalOpen(true); }}
          className="grid size-12 shrink-0 place-items-center rounded-[6px] bg-default/40 text-ink-muted transition-[background-color,color] hover:bg-accent/15 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
        >
          <PlusIcon className="size-5" />
        </button>
      </TooltipTarget>
      <Tooltip.Content showArrow placement={vertical ? 'right' : 'bottom'}>
        <Tooltip.Arrow />
        {t('ui.topbar.addServer')}
      </Tooltip.Content>
    </Tooltip>
  );

  return (
    <>
      <ServerModal open={modalOpen} onClose={() => setModalOpen(false)} initialTab={serverTab} />
      {menu && (
        <ServerContextMenu
          guildId={menu.guildId}
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          onInvite={() => setInviteGuildId(menu.guildId)}
        />
      )}
      {inviteGuildId && (
        <InviteToServerModal guildId={inviteGuildId} onClose={() => setInviteGuildId(null)} />
      )}
      {editFolder && (
        <FolderSettingsModal
          folder={editFolder}
          memberNames={editFolder.guildIds.map((id) => guilds[id]?.name).filter((n): n is string => !!n)}
          onSaved={(folder) => upsertGuildFolder(folder)}
          onClose={() => setEditFolderId(null)}
        />
      )}
      {folderMenu && (
        <>
          <div aria-hidden className="fixed inset-0 z-[59]" onClick={() => setFolderMenu(null)} />
          <div
            role="menu"
            // shadow-card, like every other context menu in the app: the skin is
            // borderless (--separator is transparent), so without the lift this
            // panel had no edge at all and the list behind it read through.
            className="bg-surface rounded-xl border border-separator shadow-card fixed z-[60] min-w-[200px] overflow-hidden py-1.5"
            style={{ left: folderMenu.x, top: folderMenu.y }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setEditFolderId(folderMenu.id);
                setFolderMenu(null);
              }}
              className="mx-1 flex w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-3 py-1.5 text-left text-sm font-light tracking-[-0.015em] transition-colors hover:bg-default/60"
            >
              <EditIcon className="size-4" />
              {t('ui.topbar.folderSettings')}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => dissolveFolder(folderMenu.id)}
              className="mx-1 flex w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-3 py-1.5 text-left text-sm font-light tracking-[-0.015em] text-danger transition-colors hover:bg-danger/15"
            >
              <TrashIcon className="size-4" />
              {t('ui.topbar.dissolveFolder')}
            </button>
          </div>
        </>
      )}
      {vertical ? (
        /* Left rail — servers stack vertically and scroll; the home
           tile pins the top, ＋ and the theme toggle pin the bottom. No "+N"
           overflow (the column just scrolls). */
        <nav
          aria-label={t('home.servers')}
          className="studio-left-rail flex h-full w-[84px] shrink-0 flex-col items-center gap-2 bg-surface py-2"
        >
          <div className="relative flex w-[72px] min-h-14 items-center justify-center">
            {currentGuildId === null && <span className={RAIL_BAR_CLASS} />}
            {dmTile}
          </div>
          <div className="scrollbar-thin flex min-h-0 w-full flex-1 flex-col items-center gap-1.5 overflow-y-auto overflow-x-hidden py-1">
            {guildFolders.map(renderFolder)}
            {visibleIds.map((id) => renderGuild(id))}
          </div>
          <div className="studio-rail-dock flex shrink-0 flex-col items-center gap-2 rounded-[6px] bg-default/30 px-1.5 py-2">
            {addButton}
            {railToggle}
          </div>
        </nav>
      ) : (
        <header
          ref={headerRef}
          className={`studio-topbar flex shrink-0 items-center gap-2.5 border-b border-separator bg-surface transition-[height,padding] ${compact ? 'h-14 px-2' : 'h-20 px-4'}`}
        >
          {dmTile}

          {/* Server avatars. They're truncated to fit (visibleIds), so the row
              never overflows horizontally — no scroll container, which also means
              the active-server halo/pill (absolutely positioned, sticks out above
              the avatar) isn't clipped. Overflowing servers collapse into the
              overlapping "+N" stack on the right.
              Avatars are draggable — the native HTML5 D&D API lets users
              drag-reorder their server rail. */}
          {/* Hard guarantee that the rail can never paint over the buttons on
              the right, whatever the arithmetic in planGuildRail concludes:
              folders always render, so a folder-heavy user on a 320 px phone
              can still ask for more than there is. `overflow-x: clip` is the
              one overflow value that pairs with `overflow-y: visible`, so
              runaway avatars are cut off horizontally while the active-server
              halo (which sticks out ABOVE the avatar) is still drawn. */}
          <div
            className="studio-server-rail flex min-w-0 items-center gap-2.5 overflow-x-clip overflow-y-visible"
            style={{
              maxWidth: tightRail ? STACK_COMPACT_PX : railMaxWidth(
                { visibleIds, overflowIds, stackPreviews, compactStack },
                foldersPx,
                headerWidth - RESERVED_FIXED_PX - actionsWidth,
              ),
            }}
          >
            {!tightRail && guildFolders.map(renderFolder)}
            {!tightRail && visibleIds.map((id) => renderGuild(id))}
            {(tightRail || overflowIds.length > 0) && (
              <ServerOverflowStack
                previewCount={tightRail ? 0 : stackPreviews}
                compact={tightRail || compactStack}
                overflowIds={tightRail ? guildOrder : overflowIds}
                guildOrder={guildOrder}
                guilds={guilds}
                currentGuildId={currentGuildId}
                unreadByGuild={unreadByGuild}
                onSelect={(id) => selectGuild(id)}
                onReorder={(draggedId, targetId, pos) => applyReorder(draggedId, targetId, pos)}
              />
            )}
          </div>

          {addButton}

          <div ref={actionsRef} className="ml-auto flex shrink-0 items-center gap-1">
            {railToggle}
          </div>
        </header>
      )}
    </>
  );
}
